import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Send, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const toUserId = searchParams.get("to");
  const itemId = searchParams.get("item");

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    loadConversations();
  }, [user, navigate]);

  useEffect(() => {
    // If coming from item detail to message a user
    if (toUserId && user) {
      const userIds = [user.id, toUserId].sort();
      let convKey = `${userIds[0]}_${userIds[1]}`;
      if (itemId) convKey += `_${itemId}`;
      setActiveConv({ conversation_id: convKey, other_user: { id: toUserId } });
      loadMessages(convKey);
    }
  }, [toUserId, itemId, user]);

  const loadConversations = async () => {
    try {
      const res = await api.get("/messages/conversations");
      setConversations(res.data);
      if (res.data.length > 0 && !toUserId) {
        setActiveConv(res.data[0]);
        loadMessages(res.data[0].conversation_id);
      }
    } catch { /* no conversations */ }
  };

  const loadMessages = async (convId) => {
    try {
      const res = await api.get(`/messages/${convId}`);
      setMessages(res.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { setMessages([]); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const receiverId = activeConv?.other_user?.id || toUserId;
      await api.post("/messages", {
        receiver_id: receiverId,
        item_id: itemId || activeConv?.item?.id || null,
        content: newMessage.trim(),
      });
      setNewMessage("");
      loadMessages(activeConv.conversation_id);
      loadConversations();
    } catch { /* error */ }
    finally { setSending(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="messages-page">
      <h1 className="font-heading font-bold text-2xl text-stone-900 mb-6">Messages</h1>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" style={{ height: "calc(100vh - 220px)" }}>
        <div className="grid grid-cols-1 md:grid-cols-3 h-full">
          {/* Conversations List */}
          <div className="border-r border-stone-200 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-stone-400 text-sm">No conversations yet</div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => { setActiveConv(conv); loadMessages(conv.conversation_id); }}
                  className={`w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-stone-50 transition-colors cursor-pointer ${
                    activeConv?.conversation_id === conv.conversation_id ? "bg-coral-light" : ""
                  }`}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">{conv.other_user?.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm text-stone-900 truncate">{conv.other_user?.name}</p>
                        {conv.unread_count > 0 && (
                          <span className="w-5 h-5 rounded-full bg-coral text-white text-xs flex items-center justify-center shrink-0">{conv.unread_count}</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 truncate">{conv.last_message?.content}</p>
                      {conv.item && <p className="text-xs text-stone-400 truncate">Re: {conv.item.title}</p>}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Messages */}
          <div className="md:col-span-2 flex flex-col h-full">
            {activeConv ? (
              <>
                {/* Header */}
                <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-3">
                  <button onClick={() => setActiveConv(null)} className="md:hidden cursor-pointer">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
                      {activeConv.other_user?.name?.[0]?.toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-sm text-stone-900">{activeConv.other_user?.name || "User"}</p>
                    {activeConv.item && <p className="text-xs text-stone-500">Re: {activeConv.item.title}</p>}
                  </div>
                </div>

                {/* Messages List */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map((msg) => {
                      const isMine = msg.sender_id === user.id;
                      return (
                        <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                            isMine ? "bg-coral text-white rounded-br-md" : "bg-stone-100 text-stone-900 rounded-bl-md"
                          }`} data-testid={`message-${msg.id}`}>
                            {msg.content}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Send */}
                <form onSubmit={sendMessage} className="p-4 border-t border-stone-200 flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="rounded-xl h-10"
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={sending} className="rounded-xl h-10 px-4 bg-coral hover:bg-coral text-white btn-press" data-testid="send-message-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-stone-400 text-sm">
                Select a conversation to start messaging
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
